// frontend/src/components/GlossaryUpload.js - Updated
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import glossaryService from '../services/glossary/glossaryService';
import '../styles/global.css';
import './DocumentUpload.css'; // Import the same CSS as DocumentUpload
import AlertMessage from './AlertMessage';

const GlossaryUpload = () => {
    const { projectId } = useParams();
    const [file, setFile] = useState(null);
    const [glossaryTerms, setGlossaryTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [fileHover, setFileHover] = useState(false);
    const navigate = useNavigate();

    // Fetch existing glossary terms when component loads
    useEffect(() => {
        const fetchGlossary = async () => {
            setLoading(true);
            setError('');
            
            try {
                const terms = await glossaryService.getGlossary(projectId);
                if (terms) {
                    setGlossaryTerms(terms);
                }
            } catch (err) {
                console.error("Error fetching glossary:", err);
                // No need to show error to user, just assume no glossary yet
            } finally {
                setLoading(false);
            }
        };

        fetchGlossary();
    }, [projectId]);

    // Auto-dismiss notifications
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const handleFileDrop = async (e) => {
        e.preventDefault();
        setFileHover(false);
        
        if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) {
            return;
        }
        
        const file = e.dataTransfer.files[0];
        
        // Check file extension
        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(fileExt)) {
            setError('Only .csv, .xlsx, and .xls files are allowed');
            return;
        }
        
        // Check file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            setError('File size exceeds 10MB limit');
            return;
        }
        
        setFile(file);
        
        // Automatically upload the file
        setUploading(true);
        setError('');
        setSuccess('');
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const response = await glossaryService.uploadGlossary(projectId, formData);
            if (response && response.terms) {
                setGlossaryTerms(response.terms);
                setSuccess("Glossary uploaded successfully");
                setFile(null);
            } else if (response && response.error) {
                setError(response.message || "Upload failed. Please try again.");
            }
        } catch (err) {
            console.error("Error uploading glossary:", err);
            setError(err.message || "Upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="container">
            <div className="back-button" onClick={() => navigate(`/projects/${projectId}`)}>
                ← Back to Project
            </div>

            <div className="card mb-6">
                <h1 className="text-2xl font-bold mb-4">Manage Translation Glossary</h1>
                <p className="mb-4 text-gray-600">
                    Upload a CSV or Excel file with source terms in the first column and target translations in the second column.
                    This glossary will be used for all translations in this project.
                </p>

                {error && (
                    <AlertMessage 
                        message={error} 
                        type="error" 
                        onDismiss={() => setError('')}
                        autoClose={true}
                        duration={3000}
                    />
                )}
                
                {success && (
                    <AlertMessage 
                        message={success} 
                        type="success" 
                        onDismiss={() => setSuccess('')}
                        autoClose={true}
                        duration={3000}
                    />
                )}

                <div className="upload-container">
                    <div 
                        className={`drop-zone ${fileHover ? 'active' : ''} ${uploading ? 'loading' : ''}`}
                        onDragOver={(e) => {
                            e.preventDefault();
                            if (!uploading) setFileHover(true);
                        }}
                        onDragEnter={(e) => {
                            e.preventDefault();
                            if (!uploading) setFileHover(true);
                        }}
                        onDragLeave={() => {
                            if (!uploading) setFileHover(false);
                        }}
                        onDrop={!uploading ? handleFileDrop : (e) => e.preventDefault()}
                    >
                        
                        <div className="drop-zone-content">
                            {uploading ? (
                                <>
                                    <svg className="upload-icon loading spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <div className="upload-text">Processing glossary...</div>
                                </>
                            ) : (
                                <>
                                    <svg className="upload-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    
                                    {file ? (
                                        <div className="upload-text">
                                            <span className="font-medium">{file.name}</span><br />
                                            <span className="text-sm text-gray-500">({(file.size / 1024).toFixed(2)} KB)</span>
                                        </div>
                                    ) : (
                                        <div className="upload-text">Drag & drop your glossary file here</div>
                                    )}
                                    
                                    <div className="upload-subtext">
                                        Accepted formats: <span className="format-badge">.csv</span> <span className="format-badge">.xlsx</span> <span className="format-badge">.xls</span>
                                        <br />Maximum size: 10MB
                                    </div>
                                </>
                            )}
                        </div>
                    </div>


                </div>
            </div>

            <div className="card">
                <h2 className="text-xl font-semibold mb-4">Current Glossary Terms</h2>
                {loading ? (
                    <div className="text-center p-4">
                        <div className="loading">Loading glossary terms...</div>
                    </div>
                ) : glossaryTerms.length > 0 ? (
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Source Term</th>
                                    <th>Target Translation</th>
                                </tr>
                            </thead>
                            <tbody>
                                {glossaryTerms.map((term, index) => (
                                    <tr key={index}>
                                        <td>{term.source}</td>
                                        <td>{term.target}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center p-6 bg-gray-50 rounded">
                        <p className="text-gray-500">
                            No glossary terms have been added yet. Upload a file to add terms.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GlossaryUpload;